import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Home, History, User, Bell, Search, Gift, Crown, Star, TrendingUp,
  Wallet, ChevronRight, ArrowUpRight, Sparkles, Award, Shield, Clock,
  Check, Phone, Mail, Heart, Diamond, Medal, Zap, ArrowLeft, CheckCircle2,
  XCircle, Calendar,
} from "lucide-react";

/* JoyCasino — luxury light-boutique theme.
   Ivory + lavender background, deep violet primary, gold accents.
   Completely different silhouette from VulkanBet (top nav instead of sidebar).
   Routes:
     /templates/joycasino/combined?page=main|history|profile
     query: filter=all|deposit|withdrawal, listpage=N, data, theme, browser */
export default function JoyCasinoCombinedPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState("main");
  const [historyFilter, setHistoryFilter] = useState("all");
  const [listPage, setListPage] = useState(1);
  const [showBrowser, setShowBrowser] = useState(true);
  const ITEMS_PER_PAGE_LIST = 12;

  useEffect(() => {
    const d = searchParams.get("data");
    const pg = searchParams.get("page");
    const f = searchParams.get("filter");
    const lp = parseInt(searchParams.get("listpage") || "1", 10);
    if (searchParams.get("browser") === "false") setShowBrowser(false);
    if (pg && ["main", "history", "profile", "settings"].includes(pg)) {
      setCurrentPage(pg === "settings" ? "profile" : pg);
    } else {
      const p = location.pathname;
      if (p.includes("/history")) setCurrentPage("history");
      else if (p.includes("/settings") || p.includes("/profile")) setCurrentPage("profile");
      else setCurrentPage("main");
    }
    if (f && ["all", "deposit", "withdrawal"].includes(f)) setHistoryFilter(f);
    if (!isNaN(lp) && lp >= 1) setListPage(lp);
    if (d) { try { setData(JSON.parse(decodeURIComponent(d))); } catch (e) { console.error(e); } }
  }, [searchParams, location.pathname]);

  useEffect(() => {
    const titles = { main: "Главная", history: "История операций", profile: "Профиль" };
    document.title = `${titles[currentPage] || "JoyCasino"} · JoyCasino`;
    document.body.style.backgroundColor = "#faf7ff";
  }, [currentPage]);

  const dateRange = useMemo(() => {
    if (!data || !data.transactions || !data.transactions.length) return { from: "", to: "" };
    const sort = (d) => {
      const p = (d || "01.01.2000").split(".");
      return parseInt(p[2]) * 10000 + parseInt(p[1]) * 100 + parseInt(p[0]);
    };
    const s = [...data.transactions].filter((t) => t.date).sort((a, b) => sort(a.date) - sort(b.date));
    return { from: s[0]?.date || "", to: s[s.length - 1]?.date || "" };
  }, [data]);

  if (!data) {
    return <div className="min-h-screen bg-[#faf7ff] flex items-center justify-center text-violet-700">Загрузка…</div>;
  }

  const accountName = `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Гость";
  const userId = data.visibleId || data.userId || "0000000";
  const balance = parseFloat(data.balance || 0);
  const bonusBalance = parseFloat(data.bonusBalance || 0);
  const fmt = (n) => parseFloat(n || 0).toLocaleString("ru-RU");

  // Casino inversion done client-side: Ozon deposit → casino "withdrawal" (user won and cashed out)
  const casinoTxs = (data.transactions || []).map((t) => {
    const inv = t.type === "deposit" ? "withdrawal" : t.type === "withdrawal" ? "deposit" : t.type;
    return { ...t, type: inv };
  });
  const filteredTx = historyFilter === "all" ? casinoTxs : casinoTxs.filter((t) => t.type === historyFilter);

  const BrowserFrame = () => (
    <div style={{ backgroundColor: "#dee1e6" }} className="flex-shrink-0">
      <div className="flex items-center h-[38px] pl-2">
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] mr-[1px]" style={{ backgroundColor: "#c9cdd4" }}>
          <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2" />
          <span className="text-[13px] text-slate-500">Ozon Карта</span>
        </div>
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px]" style={{ backgroundColor: "#ffffff" }}>
          <div className="w-4 h-4 mr-2 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)" }}>
            <Diamond className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-[13px] text-slate-800">JoyCasino — Luxury</span>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4 bg-white">
        <div className="flex gap-2">
          {["←", "→", "↻"].map((a, i) => (
            <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 text-slate-500">{a}</button>
          ))}
        </div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center bg-slate-100">
          <span className="text-[13px] text-slate-600">🔒 joy-casino.com/my/{userId}</span>
        </div>
      </div>
    </div>
  );

  // ==================== TOP NAV (horizontal) ====================
  const TopNav = () => (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-violet-100 shadow-[0_1px_3px_rgba(139,92,246,0.06)]">
      <div className="max-w-[1400px] mx-auto px-8 h-[72px] flex items-center gap-8">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-200" style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)" }}>
            <Diamond className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-serif text-[22px] font-bold text-slate-800 leading-none tracking-tight" style={{ fontFamily: "Georgia, 'Playfair Display', serif" }}>JoyCasino</div>
            <div className="text-[11px] text-violet-500 tracking-widest uppercase mt-0.5">Luxury Boutique</div>
          </div>
        </div>

        {/* Nav pills */}
        <nav className="flex items-center gap-1 ml-6">
          {[
            { id: "main", icon: Home, label: "Главная" },
            { id: "history", icon: History, label: "Операции" },
            { id: "profile", icon: User, label: "Профиль" },
          ].map((it) => {
            const active = currentPage === it.id;
            return (
              <button key={it.id} onClick={() => setCurrentPage(it.id)}
                className={`relative px-4 py-2 rounded-xl flex items-center gap-2 text-[14px] font-medium transition-all ${active ? "text-violet-700 bg-violet-50" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
                <it.icon className="w-4 h-4" />
                {it.label}
                {active && <span className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full bg-gradient-to-r from-violet-500 to-pink-500" />}
              </button>
            );
          })}
        </nav>

        <div className="flex-1" />

        {/* Search */}
        <div className="flex items-center gap-2 px-4 h-10 rounded-full bg-slate-50 border border-slate-200/70 text-slate-400 text-sm min-w-[240px]">
          <Search className="w-4 h-4" />
          <span>Поиск игры или операции…</span>
        </div>

        {/* Balance chip */}
        <div className="hidden lg:flex items-center gap-3 px-4 h-10 rounded-full" style={{ background: "linear-gradient(135deg, #faf5ff, #fdf2f8)", border: "1px solid #e9d5ff" }}>
          <Wallet className="w-4 h-4 text-violet-600" />
          <div className="text-[13px]">
            <span className="text-slate-400 mr-1">Баланс</span>
            <span className="font-semibold text-slate-800 font-mono">{fmt(balance)} ₽</span>
          </div>
        </div>

        {/* Bell + Avatar */}
        <button className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-500">
          <Bell className="w-4 h-4" />
          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-pink-500" />
        </button>
        <div className="flex items-center gap-3 pl-2 pr-1 py-1 rounded-full bg-slate-50 border border-slate-200/70">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ background: "linear-gradient(135deg, #a855f7, #ec4899)" }}>
            {(accountName[0] || "U").toUpperCase()}
          </div>
          <div className="text-[13px] text-slate-700 font-medium pr-1">{accountName.split(" ")[0]}</div>
        </div>
      </div>
    </header>
  );

  // ==================== MAIN PAGE ====================
  const MainPage = () => {
    const totalGames = data.totalGames || 0;
    const totalWins = data.totalWins || 0;
    const vipLevel = data.vipLevel || "Silver";
    const cashback = data.cashbackPercent || 10;
    const freeSpins = data.freeSpins || 0;
    const vipProgress = 67;

    return (
      <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-6">
        {/* Hero: balance + VIP */}
        <div className="grid grid-cols-[1.4fr_1fr] gap-6">
          {/* Balance card */}
          <div className="relative rounded-3xl p-7 overflow-hidden bg-white border border-violet-100 shadow-[0_20px_50px_-20px_rgba(139,92,246,0.25)]">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, #ec4899 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #a855f7 0%, transparent 70%)", transform: "translate(-30%, 30%)" }} />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-violet-500 font-semibold flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> Основной кошелёк
                </div>
                <span className="px-2.5 py-1 rounded-full text-[11px] font-medium flex items-center gap-1" style={{ background: "rgba(34,197,94,0.12)", color: "#16a34a" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Активен
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-serif text-[56px] font-bold leading-none text-slate-800 tracking-tight" style={{ fontFamily: "Georgia, serif" }}>{fmt(balance)}</span>
                <span className="text-[28px] font-serif text-violet-500 ml-1">₽</span>
              </div>
              <div className="text-sm text-slate-500 mb-6">Счёт №{userId} · RUB</div>

              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-4 rounded-2xl" style={{ background: "linear-gradient(135deg, #fdf4ff, #fce7f3)" }}>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Gift className="w-3 h-3 text-pink-500" /> Бонусы</div>
                  <div className="text-xl font-semibold text-slate-800 font-mono">{fmt(bonusBalance)} ₽</div>
                </div>
                <div className="p-4 rounded-2xl" style={{ background: "linear-gradient(135deg, #faf5ff, #ede9fe)" }}>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Zap className="w-3 h-3 text-violet-500" /> Фриспины</div>
                  <div className="text-xl font-semibold text-slate-800 font-mono">{freeSpins}</div>
                </div>
                <div className="p-4 rounded-2xl" style={{ background: "linear-gradient(135deg, #fefce8, #fef3c7)" }}>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" /> Кэшбэк</div>
                  <div className="text-xl font-semibold text-slate-800 font-mono">{cashback}%</div>
                </div>
              </div>

              <div className="flex gap-3">
                <button className="flex-1 h-12 rounded-2xl text-white font-medium text-sm flex items-center justify-center gap-2 shadow-lg shadow-violet-200 transition-all hover:shadow-xl hover:-translate-y-0.5" style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)" }}>
                  <ArrowUpRight className="w-4 h-4" /> Пополнить
                </button>
                <button className="flex-1 h-12 rounded-2xl bg-white border-2 border-violet-200 text-violet-700 font-medium text-sm hover:bg-violet-50 transition-colors">
                  Вывести
                </button>
                <button className="h-12 w-12 rounded-2xl bg-white border-2 border-pink-200 text-pink-500 flex items-center justify-center hover:bg-pink-50">
                  <Heart className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* VIP progress */}
          <div className="rounded-3xl p-7 overflow-hidden relative" style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 60%, #7c2d92 100%)" }}>
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #fbbf24 0%, transparent 70%)", transform: "translate(40%, -40%)" }} />
            <div className="relative text-white">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-4 h-4 text-amber-300" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-amber-200 font-semibold">Статус VIP</span>
              </div>
              <div className="font-serif text-[40px] font-bold leading-none mb-1 tracking-tight" style={{ fontFamily: "Georgia, serif" }}>{vipLevel}</div>
              <div className="text-sm text-violet-200 mb-6">Персональный менеджер · Кэшбэк {cashback}%</div>

              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-[12px] text-violet-200">
                  <span>До уровня Gold</span>
                  <span className="text-amber-300 font-semibold">{vipProgress}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${vipProgress}%`, background: "linear-gradient(90deg, #fbbf24, #f59e0b)" }} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-4 border-t border-white/10 text-center">
                <div>
                  <Medal className="w-4 h-4 mx-auto mb-1 text-amber-300" />
                  <div className="text-[10px] uppercase tracking-wider text-violet-200">Турниры</div>
                  <div className="font-semibold text-white mt-0.5">12</div>
                </div>
                <div>
                  <Award className="w-4 h-4 mx-auto mb-1 text-amber-300" />
                  <div className="text-[10px] uppercase tracking-wider text-violet-200">Выигрышей</div>
                  <div className="font-semibold text-white mt-0.5">{totalWins}</div>
                </div>
                <div>
                  <TrendingUp className="w-4 h-4 mx-auto mb-1 text-amber-300" />
                  <div className="text-[10px] uppercase tracking-wider text-violet-200">Ранг</div>
                  <div className="font-semibold text-white mt-0.5">#42</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Игр сыграно", value: totalGames, icon: Sparkles, grad: "from-violet-100 to-violet-50", iconClr: "text-violet-500" },
            { label: "Выигрышей", value: totalWins, icon: Star, grad: "from-pink-100 to-pink-50", iconClr: "text-pink-500" },
            { label: "Общий оборот", value: fmt(data.totalTurnover || 0) + " ₽", icon: TrendingUp, grad: "from-amber-100 to-amber-50", iconClr: "text-amber-500" },
            { label: "Лучшая серия", value: "×" + (data.bestStreak || 7), icon: Award, grad: "from-emerald-100 to-emerald-50", iconClr: "text-emerald-500" },
          ].map((s, i) => (
            <div key={i} className={`rounded-2xl p-5 bg-gradient-to-br ${s.grad} border border-white/80`}>
              <div className="flex items-center justify-between mb-3">
                <s.icon className={`w-4 h-4 ${s.iconClr}`} />
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{s.label}</span>
              </div>
              <div className="text-[26px] font-serif font-bold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Popular games — luxury pastel tiles */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-serif text-[24px] font-bold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>Избранное казино</h3>
              <p className="text-sm text-slate-500 mt-0.5">Кураторский подбор премиум-игр</p>
            </div>
            <button className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
              Каталог игр <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-6 gap-4">
            {[
              { name: "Starlight Princess", tag: "Эксклюзив", grad: "linear-gradient(135deg,#f3e8ff,#e9d5ff)", emoji: "👸" },
              { name: "Golden Key Roulette", tag: "Live", grad: "linear-gradient(135deg,#fef3c7,#fde68a)", emoji: "🔑" },
              { name: "Midnight Baccarat", tag: "VIP", grad: "linear-gradient(135deg,#ede9fe,#ddd6fe)", emoji: "🌙" },
              { name: "Cherry Boutique", tag: "Слот", grad: "linear-gradient(135deg,#fce7f3,#fbcfe8)", emoji: "🍒" },
              { name: "Emerald Garden", tag: "Слот", grad: "linear-gradient(135deg,#d1fae5,#a7f3d0)", emoji: "🌿" },
              { name: "Sapphire Nights", tag: "Live", grad: "linear-gradient(135deg,#dbeafe,#bfdbfe)", emoji: "💎" },
            ].map((g, i) => (
              <div key={i} className="rounded-2xl p-4 cursor-pointer hover:-translate-y-1 transition-all border border-white/80 shadow-sm hover:shadow-md" style={{ background: g.grad }}>
                <div className="aspect-square rounded-xl bg-white/60 backdrop-blur flex items-center justify-center mb-3 text-4xl">{g.emoji}</div>
                <div className="text-slate-800 text-[13px] font-semibold truncate">{g.name}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 text-slate-600 font-medium">{g.tag}</span>
                  <span className="text-[10px] text-slate-500 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />{(Math.random() * 4 + 2).toFixed(1)}k</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent operations preview */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-serif text-[24px] font-bold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>Последние операции</h3>
              <p className="text-sm text-slate-500 mt-0.5">Выводы выигрышей и пополнения</p>
            </div>
            <button onClick={() => setCurrentPage("history")} className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
              Вся история <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="rounded-2xl bg-white border border-violet-100 overflow-hidden divide-y divide-violet-50">
            {casinoTxs.slice(0, 5).map((t, i) => {
              const isDep = t.type === "deposit";
              return (
                <div key={i} className="p-4 flex items-center justify-between hover:bg-violet-50/40 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: isDep ? "linear-gradient(135deg, #d1fae5, #a7f3d0)" : "linear-gradient(135deg, #fce7f3, #fbcfe8)" }}>
                      {isDep ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <ArrowUpRight className="w-5 h-5 text-pink-600" />}
                    </div>
                    <div>
                      <div className="text-slate-800 text-[14px] font-medium">{isDep ? "Пополнение счёта" : "Вывод выигрыша"}</div>
                      <div className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> {t.date} {t.time || ""} · Ozon {(((t.category || "").trim() === "Финансовые услуги") ? "Карта" : (((t.category || "").trim() === "Пополнения") ? "СБП" : ((t.method === "Карта" || t.method === "СБП") ? t.method : "СБП")))}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono text-[15px] font-semibold ${isDep ? "text-emerald-600" : "text-pink-600"}`}>
                      {isDep ? "+" : "−"}{fmt(t.amount)} ₽
                    </div>
                    <div className="text-[11px] text-slate-400">#{(10000 + i * 37).toString().slice(-6)}</div>
                  </div>
                </div>
              );
            })}
            {casinoTxs.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Нет операций</div>}
          </div>
        </div>
      </div>
    );
  };

  // ==================== HISTORY PAGE ====================
  const HistoryPage = () => {
    const totalPages = Math.max(1, Math.ceil(filteredTx.length / ITEMS_PER_PAGE_LIST));
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    const sliced = filteredTx.slice((listPage - 1) * ITEMS_PER_PAGE_LIST, listPage * ITEMS_PER_PAGE_LIST);
    const totalIn = casinoTxs.filter(t => t.type === "deposit").reduce((s, t) => s + parseFloat(t.amount || 0), 0);
    const totalOut = casinoTxs.filter(t => t.type === "withdrawal").reduce((s, t) => s + parseFloat(t.amount || 0), 0);

    return (
      <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-5">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-serif text-[32px] font-bold text-slate-800 tracking-tight" style={{ fontFamily: "Georgia, serif" }}>История операций</h1>
            <p className="text-sm text-slate-500 mt-1">Все движения на счёте JoyCasino</p>
          </div>
          <button className="text-sm px-4 h-10 rounded-xl bg-white border border-violet-200 text-violet-700 font-medium hover:bg-violet-50 transition-colors">
            Экспорт PDF
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Операций", value: filteredTx.length, sub: "за период", clr: "text-slate-800" },
            { label: "Пополнения", value: `+${fmt(totalIn)} ₽`, sub: "приход", clr: "text-emerald-600" },
            { label: "Выводы", value: `−${fmt(totalOut)} ₽`, sub: "выплачено", clr: "text-pink-600" },
            { label: "Сальдо", value: `${totalIn - totalOut >= 0 ? "+" : "−"}${fmt(Math.abs(totalIn - totalOut))} ₽`, sub: "нетто", clr: totalIn - totalOut >= 0 ? "text-emerald-600" : "text-pink-600" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl p-5 bg-white border border-violet-100 shadow-sm">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{s.label}</div>
              <div className={`text-[24px] font-serif font-bold mt-1 ${s.clr}`} style={{ fontFamily: "Georgia, serif" }}>{s.value}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Filter panel */}
        <div className="rounded-2xl p-5 bg-white border border-violet-100 shadow-sm">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex bg-slate-50 rounded-full p-1">
              {[
                { id: "all", label: "Все" },
                { id: "deposit", label: "Пополнения" },
                { id: "withdrawal", label: "Выводы" },
              ].map((f) => {
                const active = historyFilter === f.id;
                return (
                  <button key={f.id} onClick={() => { setHistoryFilter(f.id); setListPage(1); }}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${active ? "text-white shadow-md shadow-violet-200" : "text-slate-500 hover:text-slate-800"}`}
                    style={active ? { background: "linear-gradient(135deg, #a855f7, #7c3aed)" } : {}}>
                    {f.label}
                  </button>
                );
              })}
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-slate-500">С:</span>
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-700 text-sm min-w-[140px] flex items-center justify-between gap-3">
                <span className="font-medium">{dateRange.from || "—"}</span>
                <Calendar className="w-3.5 h-3.5 text-violet-400" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-slate-500">По:</span>
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-700 text-sm min-w-[140px] flex items-center justify-between gap-3">
                <span className="font-medium">{dateRange.to || "—"}</span>
                <Calendar className="w-3.5 h-3.5 text-violet-400" />
              </div>
            </div>
            <button className="px-5 h-10 rounded-xl text-white text-sm font-medium shadow-md shadow-violet-200 hover:-translate-y-0.5 transition-transform" style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)" }}>
              Применить
            </button>
          </div>
        </div>

        {/* Transactions table */}
        <div className="rounded-2xl bg-white border border-violet-100 overflow-hidden shadow-sm">
          <div className="grid grid-cols-[1fr_140px_160px_160px] gap-4 px-6 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold bg-slate-50 border-b border-violet-50">
            <div>Операция</div>
            <div>Метод</div>
            <div className="text-right">Сумма</div>
            <div className="text-right">Дата / время</div>
          </div>
          {sliced.length === 0 && (
            <div className="py-16 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-violet-50 flex items-center justify-center">
                <History className="w-6 h-6 text-violet-300" />
              </div>
              <div className="text-slate-500 text-[14px]">Нет операций по выбранным фильтрам</div>
            </div>
          )}
          <div className="divide-y divide-violet-50">
            {sliced.map((t, i) => {
              const isDep = t.type === "deposit";
              const txId = (10000 + (listPage - 1) * ITEMS_PER_PAGE_LIST * 37 + i * 37 + (t.date || "").length).toString().slice(-6);
              return (
                <div key={i} className="grid grid-cols-[1fr_140px_160px_160px] gap-4 px-6 py-4 items-center hover:bg-violet-50/40 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: isDep ? "linear-gradient(135deg, #d1fae5, #a7f3d0)" : "linear-gradient(135deg, #fce7f3, #fbcfe8)" }}>
                      {isDep ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <ArrowUpRight className="w-5 h-5 text-pink-600" />}
                    </div>
                    <div>
                      <div className="text-slate-800 text-[14px] font-medium">{isDep ? "Пополнение счёта" : "Вывод выигрыша"}</div>
                      <div className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                        <Check className="w-3 h-3 text-emerald-500" /> Успешно · ID #{txId}
                      </div>
                    </div>
                  </div>
                  <div className="text-slate-600 text-[13px]">Ozon {(((t.category || "").trim() === "Финансовые услуги") ? "Карта" : (((t.category || "").trim() === "Пополнения") ? "СБП" : ((t.method === "Карта" || t.method === "СБП") ? t.method : "СБП")))}</div>
                  <div className={`text-right font-mono text-[15px] font-semibold ${isDep ? "text-emerald-600" : "text-pink-600"}`}>
                    {isDep ? "+" : "−"}{fmt(t.amount)} ₽
                  </div>
                  <div className="text-right">
                    <div className="text-slate-700 text-[13px]">{t.date}</div>
                    <div className="text-[11px] text-slate-400">{t.time || ""}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredTx.length > 0 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-violet-50 bg-slate-50/50">
              <span className="text-[13px] text-slate-500">
                Показано <span className="font-semibold text-slate-700">{(listPage - 1) * ITEMS_PER_PAGE_LIST + 1}–{Math.min(listPage * ITEMS_PER_PAGE_LIST, filteredTx.length)}</span> из <span className="font-semibold text-slate-700">{filteredTx.length}</span> операций
              </span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setListPage(Math.max(1, listPage - 1))} disabled={listPage === 1}
                  className="w-9 h-9 rounded-xl bg-white border border-slate-200 text-slate-500 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                {pages.map((p) => {
                  const active = p === listPage;
                  return (
                    <button key={p} onClick={() => setListPage(p)}
                      className={`w-9 h-9 rounded-xl text-[13px] font-semibold flex items-center justify-center transition-all ${active ? "text-white shadow-md shadow-violet-200" : "bg-white border border-slate-200 text-slate-600 hover:bg-violet-50 hover:text-violet-700"}`}
                      style={active ? { background: "linear-gradient(135deg, #a855f7, #7c3aed)" } : {}}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setListPage(Math.min(totalPages, listPage + 1))} disabled={listPage === totalPages}
                  className="w-9 h-9 rounded-xl bg-white border border-slate-200 text-slate-500 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==================== PROFILE PAGE ====================
  const ProfilePage = () => (
    <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-serif text-[32px] font-bold text-slate-800 tracking-tight" style={{ fontFamily: "Georgia, serif" }}>Личный кабинет</h1>
          <p className="text-sm text-slate-500 mt-1">Управление аккаунтом и настройками</p>
        </div>
        <button className="text-sm px-4 h-10 rounded-xl bg-white border border-violet-200 text-violet-700 font-medium hover:bg-violet-50 transition-colors">
          Редактировать
        </button>
      </div>

      <div className="grid grid-cols-[1fr_1.4fr] gap-6">
        {/* Avatar / identity card */}
        <div className="rounded-3xl p-8 bg-white border border-violet-100 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="w-28 h-28 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg shadow-violet-200" style={{ background: "linear-gradient(135deg, #a855f7, #ec4899)" }}>
                {(accountName[0] || "U").toUpperCase()}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 shadow-md">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
                  <Crown className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            <h2 className="font-serif text-[22px] font-bold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>{accountName}</h2>
            <div className="text-sm text-slate-500 mb-1">ID: {userId}</div>
            <span className="px-3 py-1 rounded-full text-[12px] font-medium flex items-center gap-1.5 mt-2" style={{ background: "linear-gradient(135deg, #fef3c7, #fde68a)", color: "#78350f" }}>
              <Shield className="w-3 h-3" /> Верифицирован · KYC OK
            </span>
            <div className="w-full mt-6 pt-6 border-t border-violet-50">
              <div className="grid grid-cols-3 text-center gap-2">
                <div>
                  <div className="text-[22px] font-serif font-bold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>{data.totalGames || 0}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-0.5">Игр</div>
                </div>
                <div>
                  <div className="text-[22px] font-serif font-bold text-emerald-600" style={{ fontFamily: "Georgia, serif" }}>{data.totalWins || 0}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-0.5">Побед</div>
                </div>
                <div>
                  <div className="text-[22px] font-serif font-bold text-pink-600" style={{ fontFamily: "Georgia, serif" }}>#42</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-0.5">Ранг</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Personal data */}
        <div className="space-y-4">
          <div className="rounded-3xl p-6 bg-white border border-violet-100 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-[18px] font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>Контактные данные</h3>
              <span className="text-[11px] uppercase tracking-wider text-violet-500 font-semibold">Приватно</span>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {[
                { icon: User, label: "ФИО", value: accountName },
                { icon: Mail, label: "E-mail", value: data.email || "—" },
                { icon: Phone, label: "Телефон", value: data.phone || "—" },
                { icon: Calendar, label: "Регистрация", value: data.registrationDate || "12.08.2024" },
              ].map((it, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <it.icon className="w-4 h-4 text-violet-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{it.label}</div>
                    <div className="text-slate-800 text-[14px] mt-0.5 truncate">{it.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* VIP card */}
          <div className="rounded-3xl p-6 overflow-hidden relative" style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)" }}>
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #fbbf24 0%, transparent 70%)", transform: "translate(40%, -40%)" }} />
            <div className="relative text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-amber-200 font-semibold mb-1">
                    <Crown className="w-3.5 h-3.5" /> VIP-программа
                  </div>
                  <div className="font-serif text-[28px] font-bold" style={{ fontFamily: "Georgia, serif" }}>{data.vipLevel || "Silver"}</div>
                </div>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-white/10 backdrop-blur">
                  <Diamond className="w-7 h-7 text-amber-300" />
                </div>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-2">
                <div className="h-full rounded-full" style={{ width: "67%", background: "linear-gradient(90deg, #fbbf24, #f59e0b)" }} />
              </div>
              <div className="flex justify-between text-[12px] text-violet-200">
                <span>67 XP / 100</span>
                <span className="text-amber-300 font-semibold">→ Gold</span>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-4 mt-4 border-t border-white/10 text-center text-[12px]">
                <div><div className="text-amber-300 font-bold text-[16px]">{data.cashbackPercent || 10}%</div><div className="text-violet-200">Кэшбэк</div></div>
                <div><div className="text-amber-300 font-bold text-[16px]">24/7</div><div className="text-violet-200">Поддержка</div></div>
                <div><div className="text-amber-300 font-bold text-[16px]">5×</div><div className="text-violet-200">Лимиты</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen template-preview-scale" style={{ background: "#faf7ff" }}>
      {showBrowser && <BrowserFrame />}
      <TopNav />
      {currentPage === "main" && <MainPage />}
      {currentPage === "history" && <HistoryPage />}
      {currentPage === "profile" && <ProfilePage />}
    </div>
  );
}
